import { useCallback } from "react";
import { useSelector } from "react-redux";
import { makeStyles } from "@mui/styles";
import clsx from "clsx";
import moment from "moment";
import {
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
} from "@mui/material";
import {
  BLANK,
  EXISTING_PLAN,
  FROM_TEMPLATE,
  PLAN,
  PLEASE_SELECT,
} from "../../../consts/strings";
import { formatDatedPlanName } from "../../../utils/plans";
import { getMatrixProfiles } from "../../../reducers/matrixProfile/selectors";
import { offsetDate } from "../../../utils/date";
import { DatePickerCalendar } from "../../DatePickerCalendar/DatePickerCalendar";
import { PlanNameInput } from "../../PlanNameInput";
import { Text } from "../../Base";
import { View } from "../../View";

const useStyles = makeStyles({
  inputContainer: {
    margin: "10px",
    marginTop: "25px",
  },
  radioContainer: {
    margin: "10px 10px 0 10px",
  },
  label: {
    fontSize: "12px",
  },
  errorLabel: {
    marginLeft: "10px",
  },
  radioLabel: {
    marginTop: "20px",
  },
  bold: {
    fontWeight: "bold",
  },
});

export const CreatePlanFirstStepContent = ({
  isDatedPlan,
  onDateChanged,
  onNameChanged,
  onBasedOnChanged,
  onSelectedPlanChanged,
  date,
  name,
  basedOn,
  selectedPlan,
  setIsNameValid,
}) => {
  const { dayProfiles, datedProfiles, templates } =
    useSelector(getMatrixProfiles);
  const styles = useStyles();

  const filterDate = useCallback(
    (date) => {
      const momentDate = offsetDate(moment(date));
      const excludedDates = [
        ...datedProfiles.map((profile) =>
          moment.utc(profile.activeDate).format("DD/MM/yyyy")
        ),
      ];
      return (
        momentDate.diff(moment.utc()) > 0 &&
        !excludedDates.includes(momentDate.format("DD/MM/yyyy"))
      );
    },
    [datedProfiles]
  );

  return (
    <View>
      {isDatedPlan && (
        <DatePickerCalendar
          filterDate={filterDate}
          onChange={onDateChanged}
          date={date}
        />
      )}
      <View className={styles.inputContainer}>
        <PlanNameInput
          name={name}
          onChange={onNameChanged}
          setIsNameValid={setIsNameValid}
        />
      </View>
      <View className={styles.radioContainer}>
        <FormControl component="fieldset" fullWidth>
          <Text className={clsx(styles.label, styles.radioLabel, styles.bold)}>
            Plan Based On
          </Text>
          <RadioGroup
            aria-label="based-on"
            name="basedOn"
            value={basedOn}
            onChange={(e) =>
              onBasedOnChanged && onBasedOnChanged(e.target.value)
            }
          >
            <FormControlLabel
              classes={{ label: styles.label }}
              value={BLANK}
              control={<Radio color={"primary"} size={"small"} />}
              label="Blank"
            />
            <FormControlLabel
              classes={{ label: styles.label }}
              value={EXISTING_PLAN}
              control={<Radio color={"primary"} size={"small"} />}
              label="Existing Plan"
              disabled={[...dayProfiles, ...datedProfiles].length === 0}
            />
            {basedOn === EXISTING_PLAN && (
              <>
                {
                  <Select
                    variant={"outlined"}
                    fullWidth
                    displayEmpty
                    renderValue={(value) =>
                      !value ? PLEASE_SELECT : value.templateName
                    }
                    value={
                      selectedPlan?.type === PLAN ? selectedPlan.value : ""
                    }
                    onChange={(e) =>
                      onSelectedPlanChanged &&
                      onSelectedPlanChanged({
                        type: PLAN,
                        value: e.target.value,
                      })
                    }
                  >
                    {[...dayProfiles, ...datedProfiles].map(
                      (profile, index) => (
                        <MenuItem key={index} value={profile}>
                          {formatDatedPlanName(profile)}
                        </MenuItem>
                      )
                    )}
                  </Select>
                }
              </>
            )}
            <FormControlLabel
              classes={{ label: styles.label }}
              value={FROM_TEMPLATE}
              control={<Radio color={"primary"} size={"small"} />}
              label="Template"
              disabled={templates.length === 0}
            />
            {basedOn === FROM_TEMPLATE && (
              <>
                {
                  <Select
                    variant={"outlined"}
                    fullWidth
                    displayEmpty
                    renderValue={(value) =>
                      !value ? PLEASE_SELECT : value.templateName
                    }
                    value={
                      selectedPlan?.type === FROM_TEMPLATE
                        ? selectedPlan.value
                        : ""
                    }
                    onChange={(e) =>
                      onSelectedPlanChanged &&
                      onSelectedPlanChanged({
                        type: FROM_TEMPLATE,
                        value: e.target.value,
                      })
                    }
                  >
                    {templates.map((profile, index) => (
                      <MenuItem key={index} value={profile}>
                        {profile.templateName}
                      </MenuItem>
                    ))}
                  </Select>
                }
              </>
            )}
          </RadioGroup>
        </FormControl>
      </View>
    </View>
  );
};
